var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Tracking } from '../internals/tracking';
import { State, } from './types';
import Logger, { LogLevelNumber, logLevelStringToNumber, } from './logger';
import EventBus, { EventScope } from '@openticket/lib-eventbus';
import OpenTicketBaseNotifications from './notifications';
// Keys used in local storage
const COOKIE_KEY = 'OPENTICKET_COOKIES';
/**
 * Base class for the order and shop class, it provides
 * default behaviour for initialization and configuration
 * management. This class should be extended.
 */
export default class OpenTicketBase extends EventScope {
    /**
     *  Constructing a new instance creates an uninitialized *(empty)* instance
     */
    constructor() {
        super(new EventBus());
        /**
         *  Boolean that reflects the initialized state of the class.
         *  @category State
         */
        this.is_initialized = false;
        /**
         *  Boolean that refrects the initializing state of the class
         *  @category State
         */
        this.state = State.Start;
        /**
         *  Boolean that reflects the loading state of the class. It
         *  is loading while setting data, both on initialization as
         *  on overwriting or updating data
         *  @category State
         */
        this.loading = true;
        /**
         * Exposed expandable settings object
         */
        this.settings = {
            colorScheme: null,
        };
        this.log = new Logger(this._eventBus);
        this.notifications = new OpenTicketBaseNotifications(this);
        // If the env is development, log all events to the console
        if (process.env.NODE_ENV === 'development') {
            this._eventBus.on([], (path, data) => {
                // Errors will already be logged separately.
                if (path.length && path[0] === 'error') {
                    return;
                }
                // Ignore the 'cart/timer/tick' events,
                // to avoid bloat in the console
                if (path.includes('timer') && path.includes('tick')) {
                    return;
                }
                if (this._config && this._config.logLevels) {
                    const level = logLevelStringToNumber[this._config.logLevels.console] || LogLevelNumber.None;
                    if (level < LogLevelNumber.Debug) {
                        return;
                    }
                }
                // eslint-disable-next-line no-console
                console.debug('EVENT', path.join('/'), data);
            });
        }
        this.tracking = new Tracking(this._eventBus);
        this.initialized = new Promise((resolve, reject) => {
            this._resolveInit = resolve;
            this._rejectInit = reject;
        });
    }
    get events() {
        return this._eventBus;
    }
    /**
     *  Listen to log events by providing a path and a callback to the `on` function
     *
     *  @param path - Path to listen to
     *  @param cb - Will be called when an error event occurs
     */
    onLog(path, cb) {
        if (typeof path === 'string') {
            path = [path];
        }
        return this.on(['log', ...path], cb, true);
    }
    /**
     * Returns true if the sdk is loaded within an iframe and is connected to an OpenTicket CrossWindowClient
     */
    get isFramed() {
        return window.top !== window.self && this.messaging.isConnected;
    }
    /**
     * Classes that extend OpenTicketBase should always call this init function
     * from their own init function.
     * @param config
     */
    initBase(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (config.gtmCode) {
                this.tracking.addGoogleTagManager(config.gtmCode);
            }
        });
    }
    /**
     *  Used to set the cookie preferences.
     */
    setCookiePreferences(preferences) {
        this.settings.cookiePreferences = preferences;
        this.emit(['cookies'], preferences);
    }
}
//# sourceMappingURL=base.js.map